import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ColabfoldParams } from '../types/colabfold';
import { RegistrationData } from '../context/AuthContextType';

// Check the current environment mode
const currentMode = import.meta.env.MODE;
// Get the API URL from environment variables (this should be set by Vite)
let API_URL = import.meta.env.VITE_API_URL || '';

// Ensure the API URL uses HTTPS
if (API_URL.startsWith('http:')) {
  API_URL = API_URL.replace('http:', 'https:');
}

console.log(`Environment: ${currentMode}`);
console.log(`API URL: ${API_URL}`);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,  // Important for cross-origin cookies
  headers: {
    'Content-Type': 'application/json',
  },
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-CSRFToken',
  // Add reasonable timeouts
  timeout: 30000, // 30 seconds default timeout
});

// Add request interceptor to log requests and ensure CSRF token is included
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Log the request for debugging
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    
    // Add timestamp to all GET requests to bypass caching
    if (config.method?.toLowerCase() === 'get') {
      const separator = config.url?.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_t=${new Date().getTime()}`;
    }
    
    // For file downloads, adjust the timeout and headers
    if (config.url?.includes('/download/')) {
      config.timeout = 60000; // 60 seconds for downloads
      
      // For blob responses, ensure proper headers
      if (config.responseType === 'blob') {
        if (config.headers) {
          config.headers['Accept'] = '*/*';
        }
      }
    }
    
    return config;
  },
  (error: unknown) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to detect HTML responses and handle errors
api.interceptors.response.use(
  response => {
    // Check if the response is HTML when we expect JSON
    if (
      response.config.url?.includes('/api/') && 
      response.headers['content-type']?.includes('text/html') &&
      typeof response.data === 'string' &&
      response.data.includes('<!DOCTYPE html>')
    ) {
      console.error('Received HTML response for API request:', response.config.url);
      return Promise.reject(new Error('Received HTML response for API request'));
    }
    
    // For blob responses, check if it's empty or too small
    if (
      response.config.responseType === 'blob' &&
      response.data instanceof Blob &&
      response.data.size < 10 // Extremely small blobs are likely errors
    ) {
      console.error('Received empty or very small blob response:', response.config.url);
      return Promise.reject(new Error('Empty or invalid file received'));
    }
    
    return response;
  },
  error => {
    // Handle network errors specifically for downloads
    if (error.code === 'ECONNABORTED' && error.config?.url?.includes('/download/')) {
      console.error('Download request timed out:', error.config.url);
      return Promise.reject(new Error('Download timed out. The server might be busy or the file might be too large.'));
    }
    
    // Handle content-length errors
    if (error.message?.includes('content') && error.message?.includes('length')) {
      console.error('Content-length error:', error.message);
      return Promise.reject(new Error('Network error while downloading. Please try again later.'));
    }
    
    return Promise.reject(error);
  }
);

// Auth service functions
export const authService = {
  async login(username: string, password: string) {
    try {
      console.log('Starting login process');
      const response = await api.post('/api/login/', { username, password });
      console.log('Login response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Login error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw error;
    }
  },

  async register(data: RegistrationData) {
    try {
      console.log('Starting registration process');
      const response = await api.post('/api/register/', data);
      console.log('Registration response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Registration error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw error;
    }
  },

  async verifyEmail(token: string) {
    try {
      console.log('Starting email verification process');
      const response = await api.post('/api/verify-email/', { token });
      console.log('Email verification response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Email verification error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw error;
    }
  },

  async resendVerificationEmail(email: string) {
    try {
      console.log('Resending verification email');
      const response = await api.post('/api/resend-verification/', { email });
      console.log('Resend verification email response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Resend verification email error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw error;
    }
  },

  async forgotPassword(email: string) {
    try {
      console.log('Requesting password reset');
      const response = await api.post('/api/forgot-password/', { email });
      console.log('Password reset request response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Password reset request error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw error;
    }
  },

  async resetPassword(token: string, new_password: string) {
    try {
      console.log('Resetting password');
      const response = await api.post('/api/reset-password/', { 
        token, 
        new_password 
      });
      console.log('Password reset response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Password reset error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw error;
    }
  },

  async logout() {
    try {
      console.log('Starting logout process');
      const response = await api.post('/api/logout/');
      console.log('Logout response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;  // Type assertion
      console.error('Logout error:', axiosError);
      console.error('Error response:', axiosError.response?.data);
      throw axiosError;
    }
  },

  async checkAuth() {
    try {
      console.log('Checking authentication status');
      const response = await api.get('/api/check-auth/');
      console.log('Auth check response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Auth check error:', error);
      // If we get a 401 Unauthorized, the user is not authenticated
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return { isAuthenticated: false, username: null };
      }
      throw error;
    }
  },

  async changePassword(currentPassword: string, newPassword: string) {
    console.log('DEBUG: Sending password change request');
    try {
      const response = await api.post('/api/change-password/', {
        oldPassword: currentPassword,
        newPassword: newPassword
      });
      console.log('DEBUG: Password change response:', response);
      return response.data;
    } catch (error) {
      console.error('DEBUG: Password change error:', error);
      throw error;
    }
  }
};

// User preferences API
export const getUserPreferences = async () => {
  const response = await api.get('/api/user-preferences/');
  return response.data;
};

export const updateUserPreferences = async (preferences: {
  job_completion: boolean;
  job_failure: boolean;
}) => {
  const response = await api.put('/api/user-preferences/', preferences);
  return response.data;
};

// Admin API functions
export const adminService = {
  async getUsageStats() {
    try {
      const response = await api.get('/api/admin-usage-stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin usage stats:', error);
      throw error;
    }
  }
};

// Colabfold API functions
export const createColabfoldJob = async (params: ColabfoldParams) => {
  const response = await api.post('/api/colabfold/jobs/', params);
  return response.data;
};

export const quotaService = {
  async getUserQuota() {
    try {
      const response = await api.get('/api/user-quota/my_quota/');
      console.log('Response from my_quota:', response.data);  // Add logging
      return response.data;  // The endpoint returns a single object directly
    } catch (error) {
      console.error('Error fetching user quota:', error);
      throw error;
    }
  },

  async updateUserQuota(quotaId: string, newQuota: number) {
    try {
      const response = await api.patch(`/api/user-quota/${quotaId}/`, { job_quota: newQuota });
      return response.data;
    } catch (error) {
      console.error('Error updating user quota:', error);
      throw error;
    }
  },

  async getAllUserQuotas() {
    try {
      const response = await api.get('/api/user-quota/');
      // The endpoint returns an array directly
      return Array.isArray(response.data) ? response.data : response.data.results || [];
    } catch (error) {
      console.error('Error fetching all user quotas:', error);
      throw error;
    }
  }
};

export default api; 